/**
 * Resilient HTTP Client with Retry and Exponential Backoff
 * Copied verbatim from the user app pattern (utils/resilientFetch.ts).
 * This is a minimal version sufficient for the admin site bootstrap.
 */

export interface RetryConfig {
  maxRetryAttempts: number;
  initialDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  useJitter: boolean;
  totalTimeout: number;
  attemptTimeout: number;
  /**
   * Opt in to retrying non-GET/HEAD requests. Only set this when the server
   * dedupes the write, and cite that idempotency at the call site (21597).
   */
  retryWrites?: boolean;
  /**
   * Keep retrying a 502/503/504 or a network error until totalTimeout is spent, instead of
   * stopping after maxRetryAttempts. GET/HEAD only, even with retryWrites (22679).
   */
  retryUntilTotalTimeout?: boolean;
}

export interface CircuitBreakerConfig {
  failureThreshold: number;
  successThreshold: number;
  timeout: number;
}

const defaultRetryConfig: RetryConfig = {
  maxRetryAttempts: 3,
  initialDelay: 1000,
  maxDelay: 30000,
  backoffMultiplier: 2,
  useJitter: true,
  totalTimeout: 45000,
  attemptTimeout: 10000,
};

/**
 * Cold-start retry budget for calls that may hit a service that's still cold
 * after a deploy (DEV runs on an oversubscribed shared B1 plan, so a
 * warm-service default aborts mid-cold-start). Never use the 10s/45s default
 * above for a call that can land right after a deploy; that default is for
 * fast DB reads on an already-warm service.
 *
 * attemptTimeout raised from 60000 to 90000 by work item 18141: the two
 * Journals-gateway-backed reads in adminPostModerationService.ts (16841) sit
 * behind the gateway's journals QoS tier, which 18011 re-measured at a 25-71s
 * cold-start band — wider than 17736's original 45000ms gateway ceiling, so
 * the first cold request through the gateway still 503'd even with this
 * budget already raised past the old ceiling. 18141 raises the gateway tier
 * to 75000ms (configuration.{env}.json, ChellusMobileGateway.api) and this
 * budget to 90000ms alongside it, so the client per-attempt timeout stays
 * strictly above the gateway's own ceiling — see GatewayQoSOptionsTests.cs
 * (API repo) for the paired assertion. totalTimeout raised to 180000 to keep
 * the same ~2x ratio the previous 60000/120000 pair had. This constant is
 * unrelated to ForSuccessFrontend's identically-named COLDSTART_RETRY_CONFIG
 * (utils/resilientFetch.ts), which backs the unrelated task-mgr gateway tier
 * and is not touched by this story.
 *
 * 22679: a cold start opens with seconds of instant platform 503s, so a fixed
 * attempt count is gone before the app is reached (19582). A GET therefore
 * retries 502/503/504 and network errors with backoff capped at maxDelay until
 * totalTimeout is spent. Its own attempt timeout stays count-bounded.
 */
export const COLDSTART_RETRY_CONFIG: Partial<RetryConfig> = {
  attemptTimeout: 90000,
  totalTimeout: 180000,
  maxRetryAttempts: 2,
  maxDelay: 5_000,
  retryUntilTotalTimeout: true,
};

/**
 * Thrown in place of the raw AbortError when resilientFetch's own
 * attemptTimeout fires. resilientFetch never threads a caller-supplied
 * AbortSignal through to fetch() (see the `{ ...init, signal: controller.signal }`
 * below), so any AbortError surfacing from fetch() here is necessarily our
 * own timeout, not something the caller aborted — safe to always translate.
 */
export class TimeoutError extends Error {
  constructor(message = 'The service is waking up after a deploy — retry in a moment.') {
    super(message);
    this.name = 'TimeoutError';
  }
}

function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException) return error.name === 'AbortError';
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function calculateDelay(attempt: number, config: RetryConfig): number {
  const base = config.initialDelay * Math.pow(config.backoffMultiplier, attempt);
  const capped = Math.min(base, config.maxDelay);
  if (config.useJitter) {
    return capped * (0.5 + Math.random() * 0.5);
  }
  return capped;
}

function resolveMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

export async function resilientFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  retryConfig?: Partial<RetryConfig>,
  _?: Partial<CircuitBreakerConfig>
): Promise<Response> {
  const merged = { ...defaultRetryConfig, ...retryConfig };
  const method = resolveMethod(input, init);
  const replayable = method === 'GET' || method === 'HEAD' || merged.retryWrites === true;
  // A replayed write can double-apply server state (21597).
  const config = replayable ? merged : { ...merged, maxRetryAttempts: 0 };
  const untilTotalTimeout =
    config.retryUntilTotalTimeout === true && (method === 'GET' || method === 'HEAD');
  let lastError: unknown;
  let lastResponse: Response | undefined;
  const startTime = Date.now();

  for (let attempt = 0; ; attempt++) {
    // Enforce totalTimeout as the overall budget: stop starting new attempts once it is
    // exhausted, and cap this attempt's own timeout to whatever remains so the last
    // attempt cannot itself run past the budget (18143 — totalTimeout was declared and
    // configured but never actually bounded anything at runtime).
    const remainingBudget = config.totalTimeout - (Date.now() - startTime);
    if (remainingBudget <= 0) {
      if (lastResponse) return lastResponse;
      throw lastError ?? new TimeoutError();
    }
    const attemptTimeoutMs = Math.min(config.attemptTimeout, remainingBudget);
    // Past maxRetryAttempts only the budget bounds the loop, so never sleep beyond it.
    const canRetry = (delay: number, extendable: boolean) =>
      attempt < config.maxRetryAttempts ||
      (untilTotalTimeout && extendable && delay < config.totalTimeout - (Date.now() - startTime));

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), attemptTimeoutMs);
      try {
        const response = await fetch(input, { ...init, signal: controller.signal });
        if (!response.ok && response.status >= 500) {
          const delay = calculateDelay(attempt, config);
          if (canRetry(delay, [502, 503, 504].includes(response.status))) {
            lastError = new Error(`HTTP ${response.status}`);
            if (untilTotalTimeout) lastResponse = response;
            await sleep(delay);
            continue;
          }
        }
        return response;
      } finally {
        clearTimeout(timeout);
      }
    } catch (error) {
      const timedOut = isAbortError(error);
      lastError = timedOut ? new TimeoutError() : error;
      const delay = calculateDelay(attempt, config);
      if (!canRetry(delay, !timedOut)) {
        throw lastError;
      }
      await sleep(delay);
    }
  }
}
